<script>
import { mapStores } from 'pinia'

import { useCartStore } from '@/cart/stores/cart'
import { parseLocalDate } from '@/utils'
import api from '@/cartapi.js'

export default {
  name: 'EventsView',

  data() {
    return {
      loading: true,
      error: null,
      events: [],
      searchQuery: '',
    }
  },

  computed: {
    ...mapStores(useCartStore),

    vendor() {
      return this.cartStore?.vendor
    },

    vendorSlug() {
      const host = window.location.hostname || ''
      const parts = host.split('.')
      return parts[0] || ''
    },

    vendorName() {
      return this.vendor?.account?.name || this.vendor?.slug || ''
    },

    heroBackgroundStyle() {
      const url = this.vendor?.branding?.backgroundUrl
      if (!url) {
        return {}
      }
      return {
        backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.4)), url(${url})`,
      }
    },

    hasActiveSearch() {
      return this.searchQuery.trim().length > 0
    },

    filteredUpcoming() {
      return this._sortedBuckets.upcoming
    },

    filteredRecent() {
      return this._sortedBuckets.recent
    },

    hasAnyEvents() {
      return this.filteredUpcoming.length > 0 || this.filteredRecent.length > 0
    },

    _sortedBuckets() {
      const events = [...this.events]
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const isPast = (event) => {
        const end = parseLocalDate(event.endDate)
        return end && end < today
      }

      const upcoming = events.filter((e) => !isPast(e))
      const recent = events.filter((e) => isPast(e))

      // Upcoming: soonest startDate first
      upcoming.sort((a, b) => {
        const da = parseLocalDate(a.startDate)
        const db = parseLocalDate(b.startDate)
        if (!da && !db) return 0
        if (!da) return 1
        if (!db) return -1
        return da - db
      })

      // Recent: most recent endDate first
      recent.sort((a, b) => {
        const da = parseLocalDate(a.endDate)
        const db = parseLocalDate(b.endDate)
        if (!da && !db) return 0
        if (!da) return 1
        if (!db) return -1
        return db - da
      })

      const query = this.searchQuery.trim().toLowerCase()
      if (!query) {
        return { upcoming, recent }
      }

      const filterFn = (event) => {
        const name = (event.name || event.slug || '').toLowerCase()
        const location = (event.locationLabel || '').toLowerCase()
        return name.includes(query) || location.includes(query)
      }

      return {
        upcoming: upcoming.filter(filterFn),
        recent: recent.filter(filterFn),
      }
    },
  },

  async created() {
    try {
      const slug = this.vendorSlug
      if (!slug) {
        this.error = 'Missing vendor slug in host'
        return
      }

      const events = await api.vendors.listEvents(slug)

      const list = Array.isArray(events) ? events : []

      this.events = list
    } catch (err) {
      console.error('Failed to load vendor/events', err)
      this.error = 'Unable to load events right now. Please try again.'
    } finally {
      this.loading = false
    }
  },

  methods: {
    onSelectEvent(event) {
      if (!event || !event.slug) {
        return
      }
      // Navigate to cart app at /{eventSlug}/
      window.location.href = `/${event.slug}/`
    },

    eventImageStyle(event) {
      const url = event && event.backgroundImage
      if (!url) {
        return {}
      }
      return {
        backgroundImage: `url(${url})`,
      }
    },


    formatCategory(event) {
      const categories = {
        festival: 'Music Festival',
        concert: 'Concert',
        conference: 'Conference',
        sporting: 'Sporting Event',
        other: 'Event',
      }
      return categories[event.category] || 'Music Festival'
    },

    formatLocation(event) {
      return event.locationLabel || 'Location TBA'
    },

    formatDateRange(event) {
      const { startDate, endDate } = event || {}
      if (!startDate && !endDate) {
        return 'Dates TBA'
      }

      const parse = parseLocalDate
      const start = parse(startDate)
      const end = parse(endDate)

      const isValid = (date) => date && !Number.isNaN(date.getTime())

      const formatFull = (date) =>
        isValid(date)
          ? date.toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : null

      if (isValid(start) && isValid(end)) {
        const sameYear = start.getFullYear() === end.getFullYear()
        const sameMonth = start.getMonth() === end.getMonth()

        const formatShort = (date, withYear) =>
          date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            ...(withYear ? { year: 'numeric' } : {}),
          })

        if (sameYear && sameMonth) {
          const year = start.getFullYear()
          return `${formatShort(start, false)} - ${end.getDate()}, ${year}`
        }

        if (sameYear) {
          const year = start.getFullYear()
          return `${formatShort(start, false)} - ${formatShort(end, false)}, ${year}`
        }

        return `${formatFull(start)} - ${formatFull(end)}`
      }

      const single = formatFull(start || end)
      return single || 'Dates TBA'
    },
  },
}
</script>

<template>
  <section class="section events-view">
    <div class="events-hero">
      <div class="events-hero__image" :style="heroBackgroundStyle"></div>
    </div>

    <div class="container">
      <div class="events-header has-text-centered">
        <h1 class="title is-4 is-spaced">
          {{ vendorName || 'Upcoming Events' }}
        </h1>
        <p class="subtitle is-6">Select your event to reserve a locker.</p>
      </div>

      <div class="events-search">
        <b-field>
          <b-input
            v-model="searchQuery"
            placeholder="Look for an event..."
            icon="magnify"
            autocomplete="off"
          />
        </b-field>
      </div>

      <div v-if="error" class="notification is-danger">
        {{ error }}
      </div>

      <div v-else>
        <div v-if="loading" class="has-text-centered py-5">Loading…</div>

        <div v-else>
          <p v-if="!hasAnyEvents" class="has-text-grey">
            <span v-if="hasActiveSearch"> No events match your search. </span>
            <span v-else> No upcoming events are available for this vendor. </span>
          </p>

          <template v-else>
            <!-- Upcoming events -->
            <div v-if="filteredUpcoming.length" class="columns is-multiline">
              <div
                v-for="event in filteredUpcoming"
                :key="event.slug || event.key"
                class="column is-12-mobile is-6-tablet is-3-desktop"
              >
                <div class="card event-card">
                  <div class="event-card__image" :style="eventImageStyle(event)">
                    <div class="event-card__pill">{{ formatCategory(event) }}</div>
                  </div>

                  <div class="card-content">
                    <p class="title is-6 mb-2">
                      {{ event.name || event.slug }}
                    </p>

                    <p class="is-size-7 has-text-grey mb-1">
                      <b-icon icon="map-marker" size="is-small" class="mr-1" />
                      {{ formatLocation(event) }}
                    </p>

                    <p class="is-size-7 has-text-grey">
                      <b-icon icon="calendar-blank" size="is-small" class="mr-1" />
                      {{ formatDateRange(event) }}
                    </p>
                  </div>

                  <div class="card-footer event-card__footer">
                    <div class="card-footer-item">
                      <b-button
                        type="is-primary"
                        size="is-small"
                        expanded
                        @click="onSelectEvent(event)"
                      >
                        <span>View Locker Options</span>
                        <b-icon icon="arrow-right" size="is-small" class="ml-2" />
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Recent events divider + cards -->
            <template v-if="filteredRecent.length">
              <div class="recent-divider">
                <span class="recent-divider__label">Recent Events</span>
              </div>

              <div class="columns is-multiline">
                <div
                  v-for="event in filteredRecent"
                  :key="event.slug || event.key"
                  class="column is-12-mobile is-6-tablet is-3-desktop"
                >
                  <div class="card event-card event-card--past">
                    <div class="event-card__image" :style="eventImageStyle(event)">
                      <div class="event-card__pill">{{ formatCategory(event) }}</div>
                    </div>

                    <div class="card-content">
                      <p class="title is-6 mb-2">
                        {{ event.name || event.slug }}
                      </p>

                      <p class="is-size-7 has-text-grey mb-1">
                        <b-icon icon="map-marker" size="is-small" class="mr-1" />
                        {{ formatLocation(event) }}
                      </p>

                      <p class="is-size-7 has-text-grey">
                        <b-icon icon="calendar-blank" size="is-small" class="mr-1" />
                        {{ formatDateRange(event) }}
                      </p>
                    </div>

                    <div class="card-footer event-card__footer">
                      <div class="card-footer-item">
                        <b-button
                          type="is-primary"
                          size="is-small"
                          expanded
                          @click="onSelectEvent(event)"
                        >
                          <span>View Locker Options</span>
                          <b-icon icon="arrow-right" size="is-small" class="ml-2" />
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.events-view {
  padding-top: 0;
}

.events-hero {
  width: 100%;
  height: 180px;
  margin: -1.5rem 0 1.5rem;
  overflow: hidden;
  background-color: #111827;
}

.events-hero__image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center center;
}

.events-header {
  margin-bottom: 1.5rem;
}

.events-search {
  max-width: 420px;
  margin: 0 auto 1.5rem;
}

.event-card {
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.event-card__image {
  position: relative;
  height: 200px;
  background-size: cover;
  background-position: center top;
}

.event-card__pill {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background-color: rgba(15, 23, 42, 0.85);
  color: #ffffff;
}

.event-card__footer {
  margin-top: auto;
}

@media (min-width: 769px) {
  .events-hero {
    height: 260px;
  }
}

.event-card--past {
  opacity: 0.55;
  filter: grayscale(30%);
  transition: opacity 0.2s, filter 0.2s;
}

.event-card--past:hover {
  opacity: 0.75;
  filter: grayscale(10%);
}

.recent-divider {
  display: flex;
  align-items: center;
  margin: 2rem 0 1.5rem;
  gap: 1rem;
}

.recent-divider::before,
.recent-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: #e5e7eb;
}

.recent-divider__label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  white-space: nowrap;
}
</style>
